import { useEffect, useState } from 'react';
import { AlertTriangle } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { formatINRSymbol } from '@/lib/format';
import { useCart } from '@/cart/useCart';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
  item: {
    id: number;
    code: string;
    name: string;
    unit: string;
    customerPrice: number;
    currentStock: number;
  } | null;
}

export function AddToCartPopup({ open, onClose, item }: Props) {
  const cart = useCart();
  const toast = useToast();
  const [qty, setQty] = useState('1');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setQty('1');
  }, [open]);

  if (!open || !item) return null;

  const parsedQty = Number(qty);
  const valid = Number.isFinite(parsedQty) && parsedQty > 0;

  async function onAdd() {
    if (!item || !valid) return;
    setSaving(true);
    try {
      await cart.add(item.id, parsedQty);
      toast.push(`Added ${parsedQty} ${item.unit} of ${item.name} to cart`, 'success');
      onClose();
    } catch (e) {
      toast.push(apiError(e, 'Could not add to cart'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[500] flex items-center justify-center p-3"
      style={{ background: 'rgba(0,0,0,0.65)' }}
    >
      <div
        className="w-full max-w-xs overflow-hidden rounded-xl shadow-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}
      >
        <div
          className="border-b px-3 py-2"
          style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}
        >
          <div className="text-[13px] font-extrabold" style={{ color: 'var(--text-primary)' }}>
            Add to Cart
          </div>
          <div className="truncate text-[10px]" style={{ color: 'var(--text-muted)' }} title={`${item.code} · ${item.name}`}>
            {item.code} · {item.name}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 px-3 py-2">
          {/* Price + Stock in a single row */}
          <div className="flex items-center justify-between gap-2 rounded-md bg-white/[0.03] px-2 py-1.5 text-[11px]">
            <span className="text-[var(--text-muted)]">Price</span>
            <span className="font-black text-accent">{formatINRSymbol(item.customerPrice)}</span>
            <span className="mx-1 h-3 w-px bg-white/10" />
            <span className="text-[var(--text-muted)]">Stock</span>
            <span className={`font-bold ${item.currentStock <= 0 ? 'text-danger' : ''}`}>
              {item.currentStock} {item.unit}
            </span>
          </div>
          {item.currentStock <= 0 && (
            <div className="rounded-md border border-warning/40 bg-warning/10 px-2 py-1 text-[10px] leading-tight text-[var(--text-secondary)]">
              <AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> Out of stock — still addable for enquiry / back-order.
            </div>
          )}
          <FormGroup label={`Quantity (${item.unit})`} required>
            <Input
              type="number"
              step="1"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && valid && !saving && onAdd()}
              autoFocus
              className="!h-8"
            />
          </FormGroup>
          <div className="flex items-center justify-between rounded-md bg-accent/[0.06] px-2 py-1.5 text-[11px] text-[var(--text-secondary)]">
            <span>Line total</span>
            <span className="text-[13px] font-black text-accent">
              {formatINRSymbol((valid ? parsedQty : 0) * item.customerPrice)}
            </span>
          </div>
        </div>
        <div
          className="flex justify-end gap-1.5 border-t px-3 py-2"
          style={{ borderColor: 'var(--border-color)', background: 'var(--bg-card)' }}
        >
          <Button variant="glass" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={onAdd} disabled={!valid || saving}>
            {saving ? 'Adding…' : 'Add to Cart'}
          </Button>
        </div>
      </div>
    </div>
  );
}
